import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ChildCaseModel} from '../../model/child-case.model';
import {TranslocoService} from '@ngneat/transloco';
import {MuacUtil} from '../../util/muac.util';
import {Router} from '@angular/router';
import algoliasearch from 'algoliasearch';
import {AlgoliaUtil} from '../../util/algolia.util';
import {LoginService} from '../../services/login.service';
import {AlgoliaService} from '../../services/algolia.service';
import {ChildCaseService} from '../../services/child-case.service';
import {FilterChildService} from '../../services/filter-child.service';
import {DateUtil} from '../../util/date.util';
import {User} from '../../model/user.model';
import {MalnutritionLevelUtil} from '../../util/malnutrition-level.util';
import {NextVisitPeriod} from '../../constants/next-visit-period';
import {ChildrenVisitsCsvContentBuilder} from '../../builder/export/csv/children/children-visits-csv-content-builder';
import {ChildrenCsvContentBuilder} from '../../builder/export/csv/children/children-csv-content-builder';
import {AngularFirestore} from '@angular/fire/firestore';
import {SymptomsService} from '../../services/symptoms.service';

export declare interface Searchable {
    search(clear: boolean);

    getFilters(): Array<string>;
}

@Component({
    selector: 'app-children',
    templateUrl: './children.component.html',
    styleUrls: ['./children.component.scss']
})
export class ChildrenComponent implements OnInit, OnDestroy, Searchable {

    order = 0;
    orders = {0: 'Próxima visita', 1: 'Nombre', 2: 'MUAC'};

    indexByName;
    indexByVisitDate;
    indexByMuac;
    indexCasesVisits;

    index;
    searchTerm = '';
    results: ChildCaseModel[] = new Array();
    searching = false;
    totalResults = 0;
    totalCases = 0;
    offlineImg = '../../assets/icon/icon-offline.svg';
    profileFilter = '';
    currPage = 0;
    numberOfPages = 0;
    hitsPerPage = 15;

    exporting = false;
    exportingProgress = 0;
    nextVisitPeriods = NextVisitPeriod.values();
    nextVisitStartDate: Date;
    nextVisitEndDate: Date;

    selectedPeriod: string;

    @Input()
    public user: User;
    customPeriodSearch = false;

    // ---- Barra de filtros (replicada de My Cases) ----
    // Dropdown próprio de "Próxima visita" (em vez de <select> nativo).
    showPeriodMenu = false;
    // Rascunho do modal de período personalizado — só vira filtro ao "Aplicar".
    periodDraftStart: Date;
    periodDraftEnd: Date;
    // Calendário próprio (bootstrap-datepicker), sempre em español.
    datepickerOpts = {
        language: 'es-ES',
        showTodayBtn: true,
        todayBtnTxt: 'Hoy',
        todayBtn: new Date(),
        todayHighlight: true,
        autoclose: true,
        format: 'dd/mm/yyyy',
        placeholder: 'dd/mm/yyyy',
        icon: 'fa fa-calendar'
    };

    // "Buscar" (botão/Enter) só dispara consulta se há texto digitado.
    private hasActiveTextSearch = false;

    // ---- Filtro por tipo de protocolo (Nuevo / Simplificado) ----
    // Igual a My Cases: aparece no modal, mas ainda inerte (newProtocol não é
    // facet configurado no Algolia). Ligar quando o facet existir.
    readonly protocolFilterEnabled = true;
    protocolFilter: 'all' | 'new' | 'old' = 'all';
    protocolFilterOptions: {key: 'all' | 'new' | 'old', label: string, description?: string}[] = [
        {
            key: 'all', label: 'Todos',
            description: 'Incluye casos de ambos protocolos: el simplificado (solo MUAC) y el nuevo ' +
                '(MUAC, peso, talla, sexo y edad).'
        },
        {
            key: 'new', label: 'Nuevo protocolo',
            description: 'Evalúa MUAC, peso, talla, sexo y edad. Es la nueva versión de la app y el portal.'
        },
        {
            key: 'old', label: 'Protocolo simplificado',
            description: 'Evalúa solo el MUAC, con cortes fijos. Corresponde a la versión anterior de la app y el portal.'
        }
    ];

    // Legenda dos pontos coloridos de "Visitas:".
    visitLegend: {class: string, label: string}[] = [
        {class: 'severe-acute-malnutrition', label: 'Desnutrición aguda severa'},
        {class: 'moderate-acute-malnutrition', label: 'Desnutrición aguda moderada'},
        {class: 'risk-of-acute-malnutrition', label: 'Riesgo de desnutrición aguda'},
        {class: 'outsite-risk-zone', label: 'Fuera de la zona de riesgo'},
        {class: 'healed', label: 'Recuperado'},
        {class: 'no-measured', label: 'Sin medición o visita futura'}
    ];

    constructor(
        private algoliaService: AlgoliaService,
        private loginService: LoginService,
        private childCaseService: ChildCaseService,
        private filterChildService: FilterChildService,
        private router: Router,
        private db: AngularFirestore,
        private translocoService: TranslocoService,
        private symptomService: SymptomsService
    ) {
        const client = algoliasearch(AlgoliaUtil.APP_ID, AlgoliaUtil.getSearchApiKey());
        this.indexByName = client.initIndex('cases_order_by_name');
        this.indexByMuac = client.initIndex('cases_muac');
        this.indexCasesVisits = client.initIndex('cases_visits');
        this.indexByVisitDate = client.initIndex(AlgoliaUtil.getChildrenIndex());
        this.childCaseService.getNextSevenDaysCases().then(result => {
        });
        filterChildService.setSearchable = this;
    }

    ngOnInit(): void {
        this.childCaseService.isNewVisit = false;
        this.algoliaService.getFilterByProfile(this.loginService.authUser).then(filter => {
            this.profileFilter = filter;
            this.getTotalCases();
            this.search(true);
        });
    }

    ngOnDestroy() {
    }

    isSupervisor() {
        return this.loginService.getCurrentUser().isSupervisor;
    }

    // Supervisor nacional (UNICEF / SNS) — vê o bloco "Ver como" SRS/DPS no
    // modal de Provincia pra simular o escopo de uma região/província.
    isNationalSupervisor() {
        return this.loginService.isCurrentUserUnicefOrSNS(this.loginService.authUser);
    }

    trackByScopeId(_index: number, item: { id: number }) {
        return item.id;
    }

    setOrder(order) {
        this.order = parseInt(order);
        this.search(true);
    }

    muac(value: number) {
        return MuacUtil.getMuacClassification(value);
    }

    showChildProfile(childCase: ChildCaseModel) {
        this.router.navigate(['app/child-profile/', childCase.token]);
    }

    getIndex() {
        switch (this.order) {
            case 0:
                return this.indexByVisitDate;
            case 1:
                return this.indexByName;
            case 2:
                return this.indexByMuac;
            default:
                return this.indexByVisitDate;
        }
    }

    getBasicFilter() {
        let basicFilter = '';
        if (this.user != null) {
            basicFilter = 'assistantUid:' + this.user.uid;
        } else {
            basicFilter = this.profileFilter;
        }

        return basicFilter;
    }

    getFilters(): Array<string> {
        let filters = new Array();

        filters = [this.getBasicFilter()];

        if (this.filterChildService.provincesSelected.length > 0) {
            filters = [...filters, this.filterChildService.provincesSelected.map(p => (`province:${p}`))];
        }

        if (this.filterChildService.countysSelected.length > 0) {
            filters = [...filters, this.filterChildService.countysSelected.map(p => (`county:${p}`))];
        }

        if (this.filterChildService.organizationSelected.length > 0) {
            filters = [...filters, this.filterChildService.organizationSelected.map(p => (`organizationName:${p}`))];
        }

        if (this.filterChildService.filtersSelected.length > 0) {
            let filtersOrs = [];
            this.filterChildService.filtersSelected.forEach(filterInt => {
                const filterObj = this.filterChildService.filterOptions.find(f => f.id == filterInt);
                if (filterInt < 4) {
                    if (filterInt == 3) {
                        filtersOrs.push('transferredCase:true');
                    } else {
                        filtersOrs.push('alert.class:' + filterObj.class);
                    }
                } else {
                    filtersOrs.push('marker:' + filterObj.class);
                }
            });
            filters = [...filters, filtersOrs];
        }

        return filters;
    }

    exportList() {
        this.exporting = true;
        this.convertToCsv().then(csvData => {
            this.exporting = false;
            const blob = new Blob(['\ufeff' + csvData], {type: 'text/csv;charset=utf-8;'});
            const dwldLink = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
            if (isSafariBrowser) {
                dwldLink.setAttribute('target', '_blank');
            }
            dwldLink.setAttribute('href', url);
            dwldLink.setAttribute('download', 'ninos-' + DateUtil.nowFormated('YYYY-MM-DD') + '.csv');
            dwldLink.style.visibility = 'hidden';
            document.body.appendChild(dwldLink);
            dwldLink.click();
            document.body.removeChild(dwldLink);
        }).catch(ex => {
            this.exporting = false;
        });
    }

    async convertToCsv(): Promise<String> {
        const ref = this;
        const titles = await this.translocoService.translateObject('children.export.titles');
        const headers = `${titles}\r\n`;
        const children = await this.algoliaService.getAllResults(
            this.getIndex(),
            this.searchTerm,
            this.getSearchConfigExport(),
            (progress) => {
                ref.exportingProgress = Math.floor(progress * 100) / 100;
            }
        );
        const data = children.flatMap(data => data.hits.map(value => ({...value})));
        const contentBuilder = new ChildrenCsvContentBuilder(data, this.translocoService);
        const csvContent = contentBuilder.build();
        return headers + csvContent;
    }

    getSearchConfig() {
        const facetFilters = this.getFilters();
        let filters = this.getMalnutritionFilters();
        if (filters.length == 0) {
            filters = this.getNextVisitPeriodFilter();
        } else if (NextVisitPeriod.ALL.key != this.selectedPeriod) {
            filters = filters.concat(' AND ' + this.getNextVisitPeriodFilter());
        }

        return {
            facets: ['token'],
            filters: filters,
            facetFilters: facetFilters,
            hitsPerPage: this.hitsPerPage,
            page: this.currPage
        };
    }

    getMalnutritionFilters() {
        let filters = '';
        if (this.filterChildService.onlyMalnutritionCases) {
            const levels = MalnutritionLevelUtil.manutritionLevels().map(level => level.description);
            for (let i = 0; i < levels.length; i++) {
                filters = filters.concat(`visits.marker:${levels[i]}`);
                if (i < (levels.length - 1)) {
                    filters = filters.concat(' OR ');
                }
            }
        }

        return filters;
    }

    getNextVisitPeriodFilter() {
        if (NextVisitPeriod.ALL.key == this.selectedPeriod) {
            return;
        }
        const date1 = new Date(this.nextVisitStartDate);
        const date2 = new Date(this.nextVisitEndDate);
        if (this.nextVisitStartDate) {
            date1.setHours(0, 0, 0);
        }

        if (this.nextVisitEndDate) {
            date2.setHours(23, 59, 59);
        }

        const nextVisitStartDate = date1.getTime();
        const nextVisitEndDate = date2.getTime();
        if (!this.selectedPeriod) {
            const inSevenDays = DateUtil.addDays(new Date(), 7);
            return `nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${inSevenDays.getTime()}`;
        }

        if (this.selectedPeriod == 'CUSTOM' && (this.nextVisitStartDate || this.nextVisitEndDate)) {
            let query = '';
            if (this.nextVisitStartDate && this.nextVisitEndDate) {
                query = `nextVisitTimestamp >= ${nextVisitStartDate} AND nextVisitTimestamp <= ${nextVisitEndDate}`;
            } else if (this.nextVisitStartDate && !this.nextVisitEndDate) {
                query = `nextVisitTimestamp >= ${nextVisitStartDate}`;
            } else if (!this.nextVisitStartDate && this.nextVisitEndDate) {
                query = `nextVisitTimestamp <= ${nextVisitEndDate}`;
            }

            return query;
        }

        switch (this.selectedPeriod) {
            case NextVisitPeriod.NEXT_SEVEN_DAYS.key:
                const inSevenDays = DateUtil.addDays(new Date(), 7);
                return `NOT marker:healed AND nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${inSevenDays.getTime()}`;
            case NextVisitPeriod.NEXT_THIRTY_DAYS.key:
                const inThirtyDays = DateUtil.addDays(new Date(), 30);
                return `NOT marker:healed AND nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${inThirtyDays.getTime()}`;
            case NextVisitPeriod.LAST_SEVEN_DAYS.key:
                const lastSevenDays = DateUtil.minusDays(new Date(), 7);
                return `NOT marker:healed AND nextVisitTimestamp >= ${lastSevenDays.getTime()} AND nextVisitTimestamp <= ${DateUtil.nowTimestamp()}`;
            case NextVisitPeriod.TODAY.key:
                return `NOT marker:healed AND nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${DateUtil.todayLastTimestampOfDay()}`;
            case NextVisitPeriod.CURRENT_MONTH.key:
                const firstDayOfMonth = DateUtil.getFirstDayOfMonth();
                const lastDayOfMonth = DateUtil.getLastDayOfMonth();
                return `NOT marker:healed AND nextVisitTimestamp >= ${firstDayOfMonth.getTime()} AND nextVisitTimestamp <= ${lastDayOfMonth.getTime()}`;
            case NextVisitPeriod.CURRENT_YEAR.key:
                const firstDayOfYear = DateUtil.getFirstDayOfYear();
                const lastDayOfYear = DateUtil.getLastDayOfYear();
                return `NOT marker:healed AND nextVisitTimestamp >= ${firstDayOfYear.getTime()} AND nextVisitTimestamp <= ${lastDayOfYear.getTime()}`;
            case NextVisitPeriod.LAST_YEAR.key:
                const firstDayOfLastYear = DateUtil.getFirstDayOfLastYear();
                const lastDayOfLastYear = DateUtil.getLastDayOfLastYear();
                return `NOT marker:healed AND nextVisitTimestamp >= ${firstDayOfLastYear.getTime()} AND nextVisitTimestamp <= ${lastDayOfLastYear.getTime()}`;
            case NextVisitPeriod.ALL.key:
                return '';
        }
    }

    getSearchConfigExport() {
        const facetFilters = this.getFilters();
        return {
            filters: this.getMalnutritionFilters(),
            facetFilters: facetFilters,
            hitsPerPage: this.hitsPerPage * 100,
            page: this.currPage
        };
    }

    cancelFilterByPeriod() {
        this.selectedPeriod = NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        this.customPeriodSearch = false;
        this.nextVisitStartDate = null;
        this.nextVisitEndDate = null;
        this.search(true);
    }

    filterByPeriod(event) {
        this.selectedPeriod = event;
        if (!this.customPeriodSearch && this.selectedPeriod == NextVisitPeriod.CUSTOM.key) {
            this.customPeriodSearch = true;
            return;
        }
        this.search(true);
    }

    // ---- Dropdown de "Próxima visita" (mesmo padrão de My Cases) ----
    get activePeriodLabel(): string {
        const key = this.selectedPeriod || NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        const found = this.nextVisitPeriods.find(p => p.key === key);
        return found ? found.label : NextVisitPeriod.NEXT_SEVEN_DAYS.label;
    }

    togglePeriodMenu() {
        this.showPeriodMenu = !this.showPeriodMenu;
        if (this.showPeriodMenu) {
            setTimeout(() => this.focusPeriodMenuItem(0));
        }
    }

    closePeriodMenu() {
        if (!this.showPeriodMenu) {
            return;
        }
        this.showPeriodMenu = false;
        const trigger = document.querySelector('.period-trigger') as HTMLElement;
        if (trigger) {
            trigger.focus();
        }
    }

    onPeriodMenuKeydown(event: KeyboardEvent) {
        if (!this.showPeriodMenu) {
            return;
        }
        const items = this.getPeriodMenuItems();
        if (!items.length) {
            return;
        }
        const current = items.indexOf(document.activeElement as HTMLElement);
        event.preventDefault();
        if (event.key === 'ArrowDown') {
            this.focusPeriodMenuItem(current < 0 ? 0 : (current + 1) % items.length);
        } else if (event.key === 'ArrowUp') {
            this.focusPeriodMenuItem(current <= 0 ? items.length - 1 : current - 1);
        }
    }

    private getPeriodMenuItems(): HTMLElement[] {
        return Array.from(document.querySelectorAll('.period-menu button')) as HTMLElement[];
    }

    private focusPeriodMenuItem(index: number) {
        const item = this.getPeriodMenuItems()[index];
        if (item) {
            item.focus();
        }
    }

    // Item do menu: CUSTOM abre o modal de datas; os demais aplicam direto.
    selectPeriodOrCustom(key: string, periodRangeModal: any) {
        if (key === NextVisitPeriod.CUSTOM.key) {
            this.prepPeriodDraft();
            if (periodRangeModal) {
                periodRangeModal.show();
            }
            return;
        }
        this.selectPeriod(key);
    }

    selectPeriod(key: string) {
        this.showPeriodMenu = false;
        this.selectedPeriod = key;
        this.customPeriodSearch = false;
        this.nextVisitStartDate = null;
        this.nextVisitEndDate = null;
        this.search(true);
    }

    // ---- Modal do período personalizado (aberto/fechado pelo template) ----
    // Prepara o rascunho antes de o modal abrir.
    prepPeriodDraft() {
        this.showPeriodMenu = false;
        this.periodDraftStart = this.nextVisitStartDate;
        this.periodDraftEnd = this.nextVisitEndDate;
    }

    applyPeriodRange() {
        this.nextVisitStartDate = this.periodDraftStart;
        this.nextVisitEndDate = this.periodDraftEnd;
        this.selectedPeriod = NextVisitPeriod.CUSTOM.key;
        this.customPeriodSearch = true;
        this.search(true);
    }

    clearPeriodRange() {
        this.selectedPeriod = NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        this.customPeriodSearch = false;
        this.nextVisitStartDate = null;
        this.nextVisitEndDate = null;
        this.periodDraftStart = null;
        this.periodDraftEnd = null;
        this.search(true);
    }

    formatPeriodRange(): string {
        const pad = (n: number) => ('0' + n).slice(-2);
        const s = this.nextVisitStartDate ? new Date(this.nextVisitStartDate) : null;
        const e = this.nextVisitEndDate ? new Date(this.nextVisitEndDate) : null;
        const dm = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
        const dmy = (d: Date) => `${dm(d)}/${d.getFullYear()}`;
        const dmy2 = (d: Date) => `${dm(d)}/${String(d.getFullYear()).slice(-2)}`;
        if (s && e) {
            return s.getFullYear() === e.getFullYear()
                ? `${dm(s)} – ${dmy(e)}`
                : `${dmy2(s)} – ${dmy2(e)}`;
        }
        if (s) {
            return `Desde ${dmy(s)}`;
        }
        if (e) {
            return `Hasta ${dmy(e)}`;
        }
        return 'Cualquier fecha';
    }

    // ---- Busca (mesmo comportamento de My Cases) ----
    clearSearch() {
        this.searchTerm = '';
        this.hasActiveTextSearch = false;
        this.search(true);
    }

    onSearchSubmit() {
        if (this.searchTerm && this.searchTerm.trim().length > 0) {
            this.hasActiveTextSearch = true;
            this.search(true);
        }
    }

    onSearchTermChange() {
        if (this.hasActiveTextSearch && (!this.searchTerm || this.searchTerm.trim().length === 0)) {
            this.hasActiveTextSearch = false;
            this.search(true);
        }
    }

    // ---- Filtro por protocolo (inerte, ver protocolFilterEnabled) ----
    setProtocolFilter(key: 'all' | 'new' | 'old') {
        this.protocolFilter = key;
        this.search(true);
    }

    async search(clear: boolean) {
        // this.childCaseService.indexAllCases();
        const requestedTerm = this.searchTerm;

        if (clear) {
            this.clearResults();
        }
        this.searching = true;
        const config = this.getSearchConfig();
        await this.getIndex().search(this.searchTerm, config).then(data => {
            this.searching = false;
            this.totalResults = data.nbHits;
            this.numberOfPages = data.nbPages;
            this.results = this.results.concat(data.hits);
        });

    }

    getTotalCases() {
        const facetFilters = [this.getBasicFilter()];
        this.indexByVisitDate.search('', {
            facetFilters: facetFilters,
            hitsPerPage: 0,
            attributesToRetrieve: [],
            attributesToHighlight: [],
            facets: []
        }).then(data => {
            this.totalCases = data.nbHits;
        });
    }

    setHitsPerPage(hitsPerPage) {
        this.hitsPerPage = hitsPerPage;
        this.search(true);
    }

    clearResults() {
        this.currPage = 0;
        this.results = [];
    }

    async findNext() {
        this.currPage = this.currPage + 1;
        this.search(false);
    }

    lastPageReached() {
        return ((this.currPage + 1) >= this.numberOfPages);
    }

    exportVisitsList() {
        this.exporting = true;
        this.convertVisitsToCsv().then(csvData => {
            this.exporting = false;
            let blob = new Blob(['\ufeff' + csvData], {type: 'text/csv;charset=utf-8;'});
            let dwldLink = document.createElement('a');
            let url = URL.createObjectURL(blob);
            let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
            if (isSafariBrowser) {
                dwldLink.setAttribute('target', '_blank');
            }
            dwldLink.setAttribute('href', url);
            dwldLink.setAttribute('download', 'ninos-visitas-' + DateUtil.nowFormated('YYYY-MM-DD') + '.csv');
            dwldLink.style.visibility = 'hidden';
            document.body.appendChild(dwldLink);
            dwldLink.click();
            document.body.removeChild(dwldLink);
        }).catch(ex => {
            this.exporting = false;
        });
    }

    async convertVisitsToCsv(): Promise<string> {
        const headers = [
            'Token del Caso',
            'MUAC',
            'Resultado',
            'Fecha',
            'Próxima Visita',
            'Hospital',
            'Semana',
            'User UID',
            'Usuario NOMBRE',
            'Usuario PROVINCIA',
            'Usuario MUNICIPIO',
            'Organización',
            'Orden de Visita',
            'Comentarios',
            'Instruciones',
            'Síntomas'
        ].join(';') + '\r\n';

        const visitResults = await this.algoliaService.getAllResults(
            this.indexCasesVisits,
            '',
            this.getCasesSearchConfigExport(),
            (progress: number) => {
                this.exportingProgress = Math.floor(progress * 100) / 100;
            }
        );

        const visitData = visitResults.flatMap(data => data.hits.map(value => ({...value})));
        const contentBuilder = new ChildrenVisitsCsvContentBuilder(visitData, this.symptomService, this.translocoService);
        const csvContent = contentBuilder.build();
        return headers + csvContent;
    }

    getCasesSearchConfigExport() {
        return {
            filters: '',
            facetFilters: [''],
            hitsPerPage: this.hitsPerPage * 100,
            attributesToRetrieve: [
                'caseId',
                'muac',
                'marker',
                'nextVisit',
                'fowardToHospital',
                'week',
                'province',
                'county',
                'organizationName',
                'gestationWeek',
                'date',
                'order',
                'healthAssistant',
                'comments',
                'instructions',
                'symptoms'
            ],
            page: 0
        };
    }

}
